/**
 * Cost Variance Chart — Pure HTML/CSS implementation
 *
 * Horizontal bar chart comparing avg tokens charged vs avg processing seconds
 * per operation. Both values normalized to percentage scale.
 * Color-coded by pricing_status.
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { OperationTimeStats } from '@/services/metricsAPI';

interface CostVarianceChartProps {
  stats: OperationTimeStats[];
}

const STATUS_COLORS: Record<string, { bar: string; text: string }> = {
  ok: { bar: 'bg-green-500/70', text: 'text-green-400' },
  overcharging: { bar: 'bg-yellow-500/70', text: 'text-yellow-400' },
  undercharging: { bar: 'bg-red-500/70', text: 'text-red-400' },
  low_confidence: { bar: 'bg-orange-500/70', text: 'text-orange-400' },
  insufficient_data: { bar: 'bg-white/20', text: 'text-white/40' },
};

const formatSeconds = (val: number): string => {
  if (val < 60) return `${val.toFixed(1)}s`;
  return `${Math.floor(val / 60)}m${(val % 60).toFixed(0)}s`;
};

export function CostVarianceChart({ stats }: CostVarianceChartProps): JSX.Element {
  const { t } = useTranslation();

  const filtered = stats.filter((s) => s.sample_count > 0).slice(0, 10);

  if (filtered.length === 0) {
    return <div className="chart-empty-state">{t('admin.metrics.noChartData')}</div>;
  }

  const maxTokens = Math.max(...filtered.map((s) => s.avg_tokens_charged), 1);
  const maxSeconds = Math.max(...filtered.map((s) => s.avg_processing_seconds), 1);

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="flex gap-4 text-xs text-white/60 mb-2">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-purple-400/80" />
          {t('admin.metrics.chartEstimated')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-cyan-400/80" />
          {t('admin.metrics.chartActual')}
        </span>
      </div>

      {filtered.map((s) => {
        const tokensPct = (s.avg_tokens_charged / maxTokens) * 100;
        const secondsPct = (s.avg_processing_seconds / maxSeconds) * 100;
        const colors = STATUS_COLORS[s.pricing_status] ?? STATUS_COLORS.insufficient_data;
        const name = s.operation.replace(/_/g, ' ');

        return (
          <div key={`${s.operation}-${s.provider ?? 'all'}`} className="group">
            {/* Operation name + status */}
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs font-medium truncate mr-2 ${colors.text}`}>
                {name}
                {s.provider && <span className="text-white/30 ml-1">({s.provider})</span>}
              </span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded border ${
                  s.pricing_status === 'ok'
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : s.pricing_status === 'undercharging'
                      ? 'bg-red-500/20 text-red-400 border-red-500/30'
                      : s.pricing_status === 'overcharging'
                        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                        : 'bg-white/10 text-white/40 border-white/10'
                }`}
              >
                {t(`admin.metrics.${s.pricing_status}`)}
              </span>
            </div>

            {/* Tokens bar */}
            <div className="flex items-center gap-2 mb-0.5">
              <div className="flex-1 h-4 bg-white/5 rounded overflow-hidden">
                <div
                  className="h-full bg-purple-400/80 rounded transition-all duration-300"
                  style={{ width: `${Math.max(tokensPct, 2)}%` }}
                />
              </div>
              <span className="text-[10px] text-white/50 w-16 text-right font-mono">
                {Math.round(s.avg_tokens_charged)} tok
              </span>
            </div>

            {/* Seconds bar */}
            <div className="flex items-center gap-2">
              <div className="flex-1 h-4 bg-white/5 rounded overflow-hidden">
                <div
                  className="h-full bg-cyan-400/80 rounded transition-all duration-300"
                  style={{ width: `${Math.max(secondsPct, 2)}%` }}
                />
              </div>
              <span className="text-[10px] text-white/50 w-16 text-right font-mono">
                {formatSeconds(s.avg_processing_seconds)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
